import React, { useCallback, useMemo, useState } from 'react';
import AutosizeInput from 'react-input-autosize';
import styled, { css } from 'styled-components';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors, usePrompt } from '../../../ui';
import { DropButton } from '../../../ui/DropButton';
import { useResolve } from '../../Container';
import { AdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession, useEditorSessionObserver } from '../../canvas/EditorSession';
import { AttachPhysical } from '../../canvas/visualization/AttachPhysical';
import { useComponentObserver } from '../../canvas/visualization/ComponentObserver';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { autoLayout } from '../autoLayout/AutoLayoutCommands';
import { Frame } from './Frame';
import { frameAdjustAddonDimensions, frameSetAddonDimensions } from './addons/FrameAddonDimensionCommands';

const XAutosizeInput = AutosizeInput as any;

const dims = (frame: Frame) => {
    const addon = frame.addon();
    if (addon?.has('dimensions')) {
        return addon?.get('dimensions') as [number, number];
    }
    return null;
};

type EdgeButtonProps = {
    type: 'add' | 'remove',
    disabled?: boolean,
    left?: string,
    right?: string,
    top?: string,
    bottom?: string,
    transform?: string,
    onAction: () => void,
};

const EdgeButtonFrame = styled.div<{ disabled?: boolean }>`
    position: absolute;
    padding: 0.125rem 0.25rem;
    font-weight: 450;
    font-size: .75rem;
    border: 2px solid ${Colors.primaryLight};
    border-radius: 4px;
    background: white;
    color: ${Colors.primaryLight};

    &:hover {
        background: ${Colors.primaryLight};
        color: white;
        cursor: pointer;
    }

    &:active {
        background: ${Colors.primary};
        color: white;
        border-color: ${Colors.primary};
    };

    ${p => p.disabled && css`
        pointer-events: none;
        opacity: 0.5;
        filter: grayscale(50%);
    `}
`;

const EdgeButton = ({ type, disabled, onAction, ...style }: EdgeButtonProps) =>
    <EdgeButtonFrame disabled={disabled} onClick={onAction} style={style}>
        <Icon size="1.25" src={`deqo://icons/hero-mini/${type === 'add' ? 'plus' : 'minus'}`} />
    </EdgeButtonFrame>;

type Dims = [number, number];
const parseDimensions = (input: string): Dims => {
    const [c, r] = input.split(/[\\\/\.,:x\-\_]/)
        .map(x => parseInt(x))
        .filter(x => !isNaN(x));
    return (!isNaN(c) && !isNaN(r)) ? [c, r] : null;
};

const DimensionsInputFrame = styled.div<{ editing?: boolean }>`
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    padding: 0.125rem;
    font-weight: 450;
    font-size: 1.25rem;
    border: 2px solid ${Colors.primaryLight};
    border-radius: 4px;
    background: white;
    color: ${Colors.primaryLight};
    
    ${p => !p.editing && css`
        &:hover {
            background: ${Colors.primaryLight};
            color: white;
            cursor: pointer;
        }

        &:active {
            background: ${Colors.primary};
            color: white;
            border-color: ${Colors.primary};
        };
    `}
`;

const DimensionsInput = React.memo<{ dimensions: Dims, onChange: (value: Dims) => void }>(({ dimensions, onChange }) => {
    const [editing, setEditing] = useState(false);
    const [next, setNext] = useState(dimensions.join('x'));

    const onApply = useCallback(() => {
        onChange(parseDimensions(next) ?? dimensions);
        setEditing(false);
    }, [dimensions, next, onChange]);

    const onInputKeyDown = useCallback((e: any) => {
        if (e.key === 'Escape') {
            setEditing(false);
            onChange(dimensions);
        }
        else if (e.key === 'Enter') {
            onApply();
        }
    }, [onChange, onApply]);

    return <DimensionsInputFrame editing={editing} onClick={() => setEditing(true)}>
        {editing && <>
            <Flexer flex="row" center gap=".125" padding="0 .125 0 .25">
                <XAutosizeInput
                    autoFocus
                    type="text"
                    value={next}
                    onKeyDown={onInputKeyDown}
                    onFocus={e => e.target.select()}
                    onChange={e => setNext(e.target.value)} />
                <DropButton overflow="0" padding="0" onClick={onApply}>
                    <Icon src="deqo://icons/hero/check" size="1.5" />
                </DropButton>
            </Flexer>
        </>}
        {!editing && next}
    </DimensionsInputFrame>;
});

export const GridAdapter = () => {

    const dispatch = useResolve(CommandDispatcher);
    const objects = useResolve(ObjectManager);
    const ai = useEditorSessionObserver(AdapterInfo);
    const updateKey = `frameAdapter:${ai.target}`;
    const session = useResolve(EditorSession);
    const subject = useMemo(() => ai.type === 'frame' ? objects.getMaybe(ai.target, Frame) : null, [ai]);
    const ticker = useComponentObserver(subject);
    const [changes, setChanges] = useState(false);
    const dimensions = useMemo(() => dims(subject), [ticker]);

    const canRemCol = dimensions[0] > 1;
    const canRemRow = dimensions[1] > 1;

    const onAdjustDimensions = (actionKey: string) => {
        objects.update(u => {
            u.set('key', updateKey);
            dispatch(frameAdjustAddonDimensions, [subject.id, actionKey]);
        });
        setChanges(true);
    };

    const onSetDimensions = useCallback(([cols, rows]: Dims) => {
        objects.update(u => {
            u.set('key', updateKey);
            dispatch(frameSetAddonDimensions, [subject.id, cols, rows]);
        });
        setChanges(true);
    }, []);

    const onClose = useCallback(() => {
        session.update(AdapterInfo, AdapterInfo.empty);
        if (changes) {
            objects.update(u => {
                u.set('key', updateKey);
                dispatch(autoLayout, [subject.id]);
            });
        }
    }, [updateKey, subject, changes]);

    const promptRef = usePrompt({
        onAction: onClose,
        ignoreEnter: true,
    });

    if (!subject) return null;

    return <AttachPhysical object={subject}>
        <Flexer ref={promptRef} absolute inset flex border={`solid .125rem ${Colors.primaryLight}`}>
            <EdgeButton type="add" left="50%" top="0" transform="translate(-50%, -100%) translate(0, -10px)"
                onAction={() => onAdjustDimensions('add-row-start')} />
            <EdgeButton type="add" left="50%" top="100%" transform="translate(-50%, 0%) translate(0, 10px)"
                onAction={() => onAdjustDimensions('add-row-end')} />
            <EdgeButton type="add" left="0" top="50%" transform="translate(-100%, -50%) translate(-10px, 0)"
                onAction={() => onAdjustDimensions('add-col-start')} />
            <EdgeButton type="add" left="100%" top="50%" transform="translate(0%, -50%) translate(10px, 0)"
                onAction={() => onAdjustDimensions('add-col-end')} />
            <EdgeButton type="remove" disabled={!canRemRow} left="50%" top="0" transform="translate(-50%, 0) translate(0, 10px)"
                onAction={() => onAdjustDimensions('rem-row-start')} />
            <EdgeButton type="remove" disabled={!canRemRow} left="50%" top="100%" transform="translate(-50%, -100%) translate(0, -10px)"
                onAction={() => onAdjustDimensions('rem-row-end')} />
            <EdgeButton type="remove" disabled={!canRemCol} left="0" top="50%" transform="translate(0, -50%) translate(10px, 0)"
                onAction={() => onAdjustDimensions('rem-col-start')} />
            <EdgeButton type="remove" disabled={!canRemCol} left="100%" top="50%" transform="translate(-100%, -50%) translate(-10px, 0)"
                onAction={() => onAdjustDimensions('rem-col-end')} />
            <DimensionsInput dimensions={dimensions}
                onChange={onSetDimensions} />
        </Flexer>
    </AttachPhysical>;
};
